import { errorHandler } from 'utilities/errorHandler'
import { api } from './index'

export type CredentialSharingAuthority = 'pending' | 'granted' | 'declined'

// Only blocking obligations are returned (status is 'pending' or 'expired').
export type CredentialSharingObligation = {
  type: 'membership' | 'license_agreement'
  status: 'pending' | 'expired'
  expires_at: string | null
}

export type AgreementAcceptanceStatusResponse = {
  // The merchant's single LPA authority decision.
  authority: CredentialSharingAuthority
  // Pointer only; the actual credentials live in 1Password, never here.
  credentials_vaulted: boolean
  // Back-compat convenience: === (authority === 'granted').
  accepted: boolean
  // LaunchDarkly kill-switch mirrored onto the response.
  mode: 'off' | 'nag' | 'block'
  obligations: CredentialSharingObligation[]
  // Server-computed: mode === 'nag' && authority !== 'granted' && obligations non-empty.
  show: boolean
}

export const credentialSharingAgreementApi = api.injectEndpoints({
  endpoints: builder => ({
    getCredentialSharingAgreementStatus: builder.query<
      AgreementAcceptanceStatusResponse,
      number | undefined
    >({
      query: () => ({ url: 'agreement_acceptance_status' }),
      providesTags: (_result, _error, merchantId) => [
        { type: 'AgreementAcceptanceStatus', id: merchantId },
      ],
      async onQueryStarted(merchantId, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch {
          errorHandler.capture(
            '[CredentialSharingAgreement] Failed to fetch agreement acceptance status',
            { merchantId }
          )
        }
      },
    }),
    // The merchant's own decision. The backend accepts only 'declined' (they'll
    // manage Apple themselves) or 'pending' (reconsider) — 'granted' authorizes
    // Thanx and requires signing the LPA, so it stays ops-confirmed.
    setCredentialSharingAuthority: builder.mutation<
      AgreementAcceptanceStatusResponse,
      { merchantId: number; authority: 'declined' | 'pending' }
    >({
      query: ({ authority }) => ({
        url: 'agreement_acceptance_status',
        method: 'PUT',
        body: { authority },
      }),
      // Refresh the status so `show`/`authority` reflect the new decision.
      invalidatesTags: (_result, _error, { merchantId }) => [
        { type: 'AgreementAcceptanceStatus', id: merchantId },
      ],
      // Mirror the GET: a dropped decision must be observable, not silently
      // lost (the modal flips the view optimistically, so a failed PUT would
      // otherwise leave the server on the old authority with no trace).
      async onQueryStarted({ merchantId, authority }, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch {
          errorHandler.capture(
            '[CredentialSharingAgreement] Failed to set agreement authority',
            { merchantId, authority }
          )
        }
      },
    }),
  }),
})

export const {
  useGetCredentialSharingAgreementStatusQuery,
  useSetCredentialSharingAuthorityMutation,
} = credentialSharingAgreementApi
